import { TextField } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import type { PhaseRangeFieldModel } from "./phaseDateTimeRangeModel";

const PHASE_DATETIME_FORMAT = "MMM D h:mm A";
const PHASE_DATE_FORMAT = "MMM D, YYYY";

const pickerPopperSlotProps = {
  strategy: "fixed" as const,
  sx: { zIndex: 10000 },
};

const pickerDesktopTrapFocusSlotProps = {
  disableEnforceFocus: true,
} as const;

const readOnlyOpenButtonSlotProps = {
  openPickerButton: {
    tabIndex: -1,
    disableRipple: true,
    "aria-hidden": true,
  },
} as const;

export type PhaseRangeFieldProps = {
  model: PhaseRangeFieldModel;
  ariaLabel: string;
  fieldSx: SxProps<Theme>;
  timezone: string;
  dateTimeFormat: string;
  dateFormat: string;
  timeSteps: { hours: number; minutes: number; seconds?: number };
  closeOnSelect: boolean;
  openIconAtStart: boolean;
};

export function PhaseRangeField({
  model,
  ariaLabel,
  fieldSx,
  timezone,
  dateTimeFormat,
  dateFormat,
  timeSteps,
  closeOnSelect,
  openIconAtStart,
}: PhaseRangeFieldProps) {
  if (model.mode === "hidden") return null;

  const readonlySx: SxProps<Theme> = {
    ...fieldSx,
    "& .MuiOutlinedInput-root .MuiInputBase-input": {
      cursor: "default",
      color: "rgba(74, 85, 104, 0.85)",
    },
  };

  const iconSlotProps = openIconAtStart
    ? ({ inputAdornment: { position: "start" as const } } as const)
    : {};

  const pickerSlotProps = {
    textField: {
      size: "small" as const,
      fullWidth: true,
      "aria-label": ariaLabel,
      sx: fieldSx,
    },
    actionBar: { actions: ["clear", "accept"] },
    ...readOnlyOpenButtonSlotProps,
    popper: pickerPopperSlotProps,
    desktopTrapFocus: pickerDesktopTrapFocusSlotProps,
    ...iconSlotProps,
  };

  switch (model.mode) {
    case "campaignBoundary":
      return (
        <TextField
          size="small"
          fullWidth
          value={model.label}
          aria-label={ariaLabel}
          InputProps={{ readOnly: true }}
          sx={readonlySx}
        />
      );
    case "readonly":
      return (
        <TextField
          size="small"
          fullWidth
          value={model.text}
          aria-label={ariaLabel}
          InputProps={{ readOnly: true }}
          sx={readonlySx}
        />
      );
    case "day":
      return (
        <DatePicker
          label=""
          timezone={timezone}
          value={model.value}
          format={dateFormat}
          formatDensity="spacious"
          minDate={model.minDate}
          maxDate={model.maxDate}
          closeOnSelect
          /** @ts-expect-error - MUI types are not fully compatible with our custom slotProps. */
          slotProps={{
            ...pickerSlotProps,
            textField: {
              ...pickerSlotProps.textField,
              placeholder: model.placeholder,
            },
          }}
          onChange={model.onChange}
        />
      );
    case "dateTime":
      return (
        <DateTimePicker
          label=""
          timezone={timezone}
          value={model.value}
          format={dateTimeFormat}
          formatDensity="spacious"
          minDateTime={model.minDateTime}
          maxDateTime={model.maxDateTime}
          ampm
          timeSteps={timeSteps}
          closeOnSelect={closeOnSelect}
          /** @ts-expect-error - MUI types are not fully compatible with our custom slotProps. */
          slotProps={{
            ...pickerSlotProps,
            textField: {
              ...pickerSlotProps.textField,
              placeholder: model.placeholder,
            },
          }}
          onChange={model.onChange}
        />
      );
  }
}

export const phaseRangeFieldFormats = {
  dateTime: PHASE_DATETIME_FORMAT,
  date: PHASE_DATE_FORMAT,
};
